import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from "./Header";
import FilterBar from "./FilterBar";
import UserList from "./UserList";
import UserDetailModal from "./UserDetailModal";
import UserEditModal from "./UserEditModal";
import UserPermissionModal from "./UserPermissionModal";
import styles from './UserManagement.module.css';

const UserManagement = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.loginData.loginData);
  
  // Eğer form kullanıcısı ise, dashboard'a yönlendir
  useEffect(() => {
    // Redux state'ten kontrol et
    if (userData && userData.userType === 'formUser') {
      navigate('/user-dashboard');
      return;
    }
    
    // localStorage'dan da kontrol et
    const formUserToken = localStorage.getItem('formUserToken');
    const formUserData = localStorage.getItem('formUserData');
    
    if (formUserToken && formUserData) {
      try {
        const parsedUserData = JSON.parse(formUserData);
        if (parsedUserData && parsedUserData.id) {
          navigate('/user-dashboard');
          return;
        }
      } catch (error) {
        console.error('Error parsing stored form user data:', error);
      }
    }
  }, [userData, navigate]);
  
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "Admin Kullanıcı",
      email: "admin@example.com",
      role: "admin",
      status: "active",
      lastLogin: "2024-03-20",
      permissions: ["all"],
      department: "Yönetim",
      createdAt: "2024-01-01",
      activities: [
        { date: "2024-03-20", action: "Giriş yapıldı", details: "Başarılı giriş" },
        { date: "2024-03-19", action: "Ayarlar güncellendi", details: "Profil bilgileri güncellendi" }
      ],
      notes: [],
      tags: [],
      avatar: null,
      files: [],
      notifications: [],
      reminders: [],
      todos: [],
      auditTrail: []
    },
    {
      id: 2,
      name: "Süper Kullanıcı",
      email: "super@example.com",
      role: "superuser",
      status: "active",
      lastLogin: "2024-03-19",
      permissions: ["create_form", "edit_form", "delete_form"],
      department: "IT",
      createdAt: "2024-01-15",
      activities: [
        { date: "2024-03-19", action: "Form oluşturuldu", details: "Yeni form oluşturuldu" }
      ],
      notes: [],
      tags: [],
      avatar: null,
      files: [],
      notifications: [],
      reminders: [],
      todos: [],
      auditTrail: []
    },
    {
      id: 3,
      name: "Normal Kullanıcı",
      email: "user@example.com",
      role: "user",
      status: "inactive",
      lastLogin: "2024-03-18",
      permissions: ["view_form"],
      department: "Satış",
      createdAt: "2024-02-01",
      activities: [],
      notes: [],
      tags: [],
      avatar: null,
      files: [],
      notifications: [],
      reminders: [],
      todos: [],
      auditTrail: []
    }
  ]);

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    department: '',
    sort: ''
  });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkRole, setBulkRole] = useState('user');
  const [showBulkPermissionModal, setShowBulkPermissionModal] = useState(false);
  const [bulkPermissions, setBulkPermissions] = useState({
    form: [
      { name: 'Form Görüntüleme', granted: false },
      { name: 'Form Oluşturma', granted: false },
      { name: 'Form Düzenleme', granted: false },
      { name: 'Form Silme', granted: false }
    ],
    user: [
      { name: 'Kullanıcı Görüntüleme', granted: false },
      { name: 'Kullanıcı Ekleme', granted: false },
      { name: 'Kullanıcı Düzenleme', granted: false },
      { name: 'Kullanıcı Silme', granted: false }
    ]
  });
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [bulkEmailLog, setBulkEmailLog] = useState(null);
  const [currentAdmin] = useState({ id: 100, name: 'Admin Paneli', email: 'admin@panel.com' });
  const [departments, setDepartments] = useState(["Yönetim", "IT", "Satış"]);

  // Arama inputu için ref
  const searchInputRef = useRef(null);

  // Kısayollar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'n' || e.key === 'N') {
        // Yeni kullanıcı ekle
        const addBtn = document.querySelector('button[title="Yeni Kullanıcı"]');
        if (addBtn) addBtn.click();
      } else if (e.key === 'f' || e.key === 'F') {
        // Arama inputuna odaklan
        if (searchInputRef.current) searchInputRef.current.focus();
      } else if (e.key === 'Escape') {
        // Açık modalı kapat
        const closeBtn = document.querySelector('button[title="Kapat"]');
        if (closeBtn) closeBtn.click();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, filters]);

  const filterUsers = () => {
    let filtered = [...users];
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.department.toLowerCase().includes(searchLower)
      );
    }
    
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }
    
    if (filters.status) {
      filtered = filtered.filter(user => user.status === filters.status);
    }

    if (filters.department) {
      filtered = filtered.filter(user => user.department === filters.department);
    }

    // Sıralama
    if (filters.sort) {
      filtered.sort((a, b) => {
        if (filters.sort === 'name') {
          return a.name.localeCompare(b.name, 'tr');
        } else if (filters.sort === 'role') {
          return a.role.localeCompare(b.role, 'tr');
        } else if (filters.sort === 'createdAt') {
          return new Date(a.createdAt) - new Date(b.createdAt);
        }
        return 0;
      });
    }
    
    setFilteredUsers(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleUserAction = (action, user) => {
    setSelectedUser(user);
    setActiveModal(action);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedUser(null);
  };

  // Yardımcı log ekleme fonksiyonu
  const addActivity = (user, action, details) => {
    const newActivity = {
      date: new Date().toISOString(),
      action,
      details,
      by: currentAdmin.name
    };
    return {
      ...user,
      activities: user.activities ? [newActivity, ...user.activities] : [newActivity],
      auditTrail: [...(user.auditTrail || []), newActivity]
    };
  };

  const handleSaveUser = (updatedUser) => {
    setUsers(prev => prev.map(user =>
      user.id === updatedUser.id
        ? addActivity(updatedUser, 'Kullanıcı bilgileri güncellendi', 'Profil veya rol/durum değişikliği yapıldı')
        : user
    ));
    handleCloseModal();
  };

  const handleSavePermissions = (permissions) => {
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? addActivity({ ...user, permissions }, 'İzinler güncellendi', 'Kullanıcı izinleri değiştirildi')
        : user
    ));
    handleCloseModal();
  };

  const handleAddUser = (newUser) => {
    setUsers(prev => [...prev, { ...newUser, notes: [], tags: [], avatar: newUser.avatar || null, files: [], notifications: [], reminders: [], todos: [], auditTrail: [] }]);
  };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      setUsers(prev => prev
        .filter(user => user.id !== userId)
      );
      // Silinen kullanıcıya log eklenemez ama isterseniz ayrı bir log dizisi tutabilirsiniz.
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUserIds(filteredUsers.map(user => user.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedUserIds.length === 0) return;
    if (window.confirm('Seçili kullanıcıları silmek istediğinizden emin misiniz?')) {
      setUsers(prev => prev.filter(user => !selectedUserIds.includes(user.id)));
      setSelectedUserIds([]);
      // Silinen kullanıcılar için log eklenemez.
    }
  };

  const handleBulkRoleChange = (e) => {
    setBulkRole(e.target.value);
  };

  const handleBulkRoleSave = () => {
    setUsers(prev => prev.map(user =>
      selectedUserIds.includes(user.id)
        ? addActivity({ ...user, role: bulkRole }, 'Toplu rol değişikliği', `Rolü ${bulkRole} olarak değiştirildi`)
        : user
    ));
    setShowBulkRoleModal(false);
    setSelectedUserIds([]);
  };

  const handleBulkPermissionChange = (category, index) => {
    setBulkPermissions(prev => ({
      ...prev,
      [category]: prev[category].map((perm, i) =>
        i === index ? { ...perm, granted: !perm.granted } : perm
      )
    }));
  };

  const handleBulkPermissionSave = () => {
    setUsers(prev => prev.map(user =>
      selectedUserIds.includes(user.id)
        ? addActivity({ ...user, permissions: bulkPermissions }, 'Toplu izin değişikliği', 'İzinler topluca değiştirildi')
        : user
    ));
    setShowBulkPermissionModal(false);
    setSelectedUserIds([]);
  };

  const handleBulkEmailSend = (e) => {
    e.preventDefault();
    const emails = users.filter(user => selectedUserIds.includes(user.id)).map(user => user.email);
    setUsers(prev => prev.map(user =>
      selectedUserIds.includes(user.id)
        ? addActivity(user, 'Toplu e-posta gönderildi', `Konu: ${bulkEmailSubject}`)
        : user
    ));
    setBulkEmailLog({
      subject: bulkEmailSubject,
      message: bulkEmailMessage,
      emails
    });
    setShowBulkEmailModal(false);
    setBulkEmailSubject('');
    setBulkEmailMessage('');
    setSelectedUserIds([]);
  };

  // Not ekleme ve silme fonksiyonları
  const handleAddNote = (note) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, notes: [...(user.notes || []), note] }
        : user
    ));
  };
  const handleDeleteNote = (noteIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, notes: user.notes.filter((_, idx) => idx !== noteIdx) }
        : user
    ));
  };

  // Etiket ekleme ve silme fonksiyonları
  const handleAddTag = (tag) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, tags: [...(user.tags || []), tag] }
        : user
    ));
  };
  const handleDeleteTag = (tagIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, tags: user.tags.filter((_, idx) => idx !== tagIdx) }
        : user
    ));
  };

  // Dosya ekleme ve silme fonksiyonları
  const handleAddFile = (file) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, files: [...(user.files || []), file] }
        : user
    ));
  };
  const handleDeleteFile = (fileIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, files: user.files.filter((_, idx) => idx !== fileIdx) }
        : user
    ));
  };

  // Bildirim ekleme ve silme fonksiyonları
  const handleAddNotification = (notif) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, notifications: [...(user.notifications || []), notif] }
        : user
    ));
  };
  const handleDeleteNotification = (notifIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, notifications: user.notifications.filter((_, idx) => idx !== notifIdx) }
        : user
    ));
  };

  // Hatırlatıcı ekleme ve silme fonksiyonları
  const handleAddReminder = (reminder) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, reminders: [...(user.reminders || []), reminder] }
        : user
    ));
  };
  const handleDeleteReminder = (remIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, reminders: user.reminders.filter((_, idx) => idx !== remIdx) }
        : user
    ));
  };

  // Görev ekleme, tamamlama ve silme fonksiyonları
  const handleAddTodo = (todo) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, todos: [...(user.todos || []), todo] }
        : user
    ));
  };
  const handleToggleTodo = (todoIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, todos: user.todos.map((t, i) => i === todoIdx ? { ...t, done: !t.done } : t) }
        : user
    ));
  };
  const handleDeleteTodo = (todoIdx) => {
    if (!selectedUser) return;
    setUsers(prev => prev.map(user =>
      user.id === selectedUser.id
        ? { ...user, todos: user.todos.filter((_, idx) => idx !== todoIdx) }
        : user
    ));
  };

  // CSV dışa aktarma fonksiyonu
  const handleExportCSV = () => {
    const csvRows = [];
    // Başlıklar
    csvRows.push([
      'Ad Soyad', 'E-posta', 'Rol', 'Durum', 'Departman', 'Oluşturulma', 'Son Giriş', 'Etiketler'
    ].join(','));
    // Satırlar
    filteredUsers.forEach(user => {
      csvRows.push([
        user.name,
        user.email,
        user.role,
        user.status,
        user.department,
        user.createdAt,
        user.lastLogin,
        (user.tags || []).join('; ')
      ].map(val => `"${val}"`).join(','));
    });
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kullanicilar.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddDepartment = (dep) => {
    if (dep && !departments.includes(dep)) setDepartments(prev => [...prev, dep]);
  };
  const handleDeleteDepartment = (dep) => {
    setDepartments(prev => prev.filter(d => d !== dep));
    // Kullanıcıların departmanını da güncelleyebilirsin (isteğe bağlı)
  };

  // İstatistikler
  const total = users.length;
  const active = users.filter(u => u.status === 'active').length;
  const inactive = users.filter(u => u.status === 'inactive').length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const superuserCount = users.filter(u => u.role === 'superuser').length;
  const userCount = users.filter(u => u.role === 'user').length;
  const departmentStats = departments.map(dep => ({
    name: dep,
    count: users.filter(u => u.department === dep).length
  }));

  return (
    <div className={styles.container}>
      <Header onAddUser={handleAddUser} departments={departments} onAddDepartment={handleAddDepartment} onDeleteDepartment={handleDeleteDepartment} />
      {/* Kısayol yardım kutusu */}
      <div style={{ textAlign: 'right', marginBottom: 8, color: '#64748b', fontSize: 13 }}>
        <i className="fas fa-keyboard"></i> Kısayollar: <b>N</b> = Yeni Kullanıcı, <b>F</b> = Ara, <b>ESC</b> = Modal Kapat
      </div>
      <FilterBar onFilterChange={handleFilterChange} departments={departments} searchInputRef={searchInputRef} />
      
      <UserList 
        users={filteredUsers}
        onDetail={(user) => handleUserAction('detail', user)}
        onEdit={(user) => handleUserAction('edit', user)}
        onPermission={(user) => handleUserAction('permission', user)}
        onDelete={handleDeleteUser}
        selectedUserIds={selectedUserIds}
        onSelectUser={handleSelectUser}
        onSelectAll={handleSelectAll}
      />

      {selectedUserIds.length > 0 && (
        <div style={{ margin: '1rem 0', textAlign: 'right', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            className={`${styles.button} ${styles.deleteButton}`}
            onClick={handleDeleteSelected}
          >
            Seçili Kullanıcıları Sil
          </button>
          <button
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={() => setShowBulkRoleModal(true)}
          >
            Toplu Rol Değiştir
          </button>
          <button
            className={`${styles.button} ${styles.permissionButton}`}
            onClick={() => setShowBulkPermissionModal(true)}
          >
            Toplu İzin Değiştir
          </button>
          <button
            className={`${styles.button} ${styles.primaryButton}`}
            onClick={() => setShowBulkEmailModal(true)}
          >
            Toplu E-posta Gönder
          </button>
        </div>
      )}

      <UserDetailModal
        open={activeModal === 'detail'}
        user={selectedUser}
        onClose={handleCloseModal}
        onAddNote={handleAddNote}
        onDeleteNote={handleDeleteNote}
        onAddTag={handleAddTag}
        onDeleteTag={handleDeleteTag}
        onAddFile={handleAddFile}
        onDeleteFile={handleDeleteFile}
        onAddNotification={handleAddNotification}
        onDeleteNotification={handleDeleteNotification}
        onAddReminder={handleAddReminder}
        onDeleteReminder={handleDeleteReminder}
        onAddTodo={handleAddTodo}
        onToggleTodo={handleToggleTodo}
        onDeleteTodo={handleDeleteTodo}
      />

      <UserEditModal
        open={activeModal === 'edit'}
        user={selectedUser}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
      />

      <UserPermissionModal
        open={activeModal === 'permission'}
        user={selectedUser}
        onClose={handleCloseModal}
        onSave={handleSavePermissions}
      />

      {/* Toplu rol değiştirme modalı */}
      {showBulkRoleModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Toplu Rol Değiştir</h2>
              <button className={styles.closeButton} onClick={() => setShowBulkRoleModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="bulkRole">Yeni Rol</label>
              <select
                id="bulkRole"
                className={styles.select}
                value={bulkRole}
                onChange={handleBulkRoleChange}
              >
                <option value="user">Normal Kullanıcı</option>
                <option value="superuser">Süper Kullanıcı</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className={styles.modalActions}>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => setShowBulkRoleModal(false)}
              >
                İptal
              </button>
              <button
                className={`${styles.button} ${styles.primaryButton}`}
                onClick={handleBulkRoleSave}
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplu izin değiştirme modalı */}
      {showBulkPermissionModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Toplu İzin Değiştir</h2>
              <button className={styles.closeButton} onClick={() => setShowBulkPermissionModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleBulkPermissionSave(); }}>
              <div className={styles.permissionSection}>
                <h3 className={styles.sectionTitle}>Form İzinleri</h3>
                <div className={styles.permissionList}>
                  {bulkPermissions.form.map((permission, idx) => (
                    <label key={idx} className={styles.permissionLabel} style={{ marginBottom: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={permission.granted}
                        onChange={() => handleBulkPermissionChange('form', idx)}
                      />
                      {permission.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.permissionSection}>
                <h3 className={styles.sectionTitle}>Kullanıcı İzinleri</h3>
                <div className={styles.permissionList}>
                  {bulkPermissions.user.map((permission, idx) => (
                    <label key={idx} className={styles.permissionLabel} style={{ marginBottom: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={permission.granted}
                        onChange={() => handleBulkPermissionChange('user', idx)}
                      />
                      {permission.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.secondaryButton}`}
                  onClick={() => setShowBulkPermissionModal(false)}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.primaryButton}`}
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toplu e-posta gönderme modalı */}
      {showBulkEmailModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Toplu E-posta Gönder</h2>
              <button className={styles.closeButton} onClick={() => setShowBulkEmailModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleBulkEmailSend}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="bulkEmailSubject">Konu</label>
                <input
                  id="bulkEmailSubject"
                  className={styles.input}
                  value={bulkEmailSubject}
                  onChange={e => setBulkEmailSubject(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="bulkEmailMessage">Mesaj</label>
                <textarea
                  id="bulkEmailMessage"
                  className={styles.input}
                  value={bulkEmailMessage}
                  onChange={e => setBulkEmailMessage(e.target.value)}
                  rows={5}
                  required
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.secondaryButton}`}
                  onClick={() => setShowBulkEmailModal(false)}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.primaryButton}`}
                >
                  Gönder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* E-posta gönderim logu */}
      {bulkEmailLog && (
        <div style={{ margin: '1rem 0', background: '#f1f5f9', borderRadius: 8, padding: '1rem', color: '#475569' }}>
          <strong>E-posta gönderildi!</strong><br />
          <span>Konu: {bulkEmailLog.subject}</span><br />
          <span>Mesaj: {bulkEmailLog.message}</span><br />
          <span>Alıcılar: {bulkEmailLog.emails.join(', ')}</span>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 